// services/omoreCohortAnalysisService.ts
// Cohort analysis for user retention and engagement tracking

import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type { OmoreSession } from '../types/OmoreInteractionTypes';

// ─── Collection Path ────────────────────────────────────────

const SESSIONS_PATH = 'omoreInteractions/sessions/items';

// ─── Types ──────────────────────────────────────────────────

export enum CohortPeriod {
    WEEKLY = 'weekly',
    MONTHLY = 'monthly',
}

export enum EngagementTier {
    POWER_USER = 'power_user',
    ACTIVE = 'active',
    REGULAR = 'regular',
    TRIAL = 'trial',
    CHURNED = 'churned',
}

export const EngagementTierLabels: Record<EngagementTier, string> = {
    [EngagementTier.POWER_USER]: 'Power User (10+)',
    [EngagementTier.ACTIVE]: 'Active (5-9)',
    [EngagementTier.REGULAR]: 'Regular (2-4)',
    [EngagementTier.TRIAL]: 'Trial (1)',
    [EngagementTier.CHURNED]: 'Churned (30d+)',
};

export const EngagementTierColors: Record<EngagementTier, string> = {
    [EngagementTier.POWER_USER]: '#10b981',
    [EngagementTier.ACTIVE]: '#3b82f6',
    [EngagementTier.REGULAR]: '#f59e0b',
    [EngagementTier.TRIAL]: '#8b5cf6',
    [EngagementTier.CHURNED]: '#ef4444',
};

export interface UserCohortData {
    appleID: string;
    explorerName: string;
    firstSessionDate: string;
    lastSessionDate: string;
    totalSessions: number;
    totalMessages: number;
    totalTokens: number;
    avgSessionDuration: number;
    helperTypesUsed: string[];
    engagementTier: EngagementTier;
    daysSinceFirstSession: number;
    daysSinceLastSession: number;
    retentionRate: number;
}

export interface Cohort {
    cohortId: string;
    cohortLabel: string;
    startDate: string;
    endDate: string;
    userCount: number;
    users: UserCohortData[];
    retention: {
        week1: number;
        week2: number;
        week3: number;
        week4: number;
        month2: number;
        month3: number;
    };
    avgSessionsPerUser: number;
    avgMessagesPerUser: number;
    avgTokensPerUser: number;
    powerUserPercent: number;
    churnedUserPercent: number;
    topHelperTypes: Array<{ helperType: string; userCount: number; percentage: number }>;
}

export interface CohortAnalysis {
    period: CohortPeriod;
    startDate: string;
    endDate: string;
    cohorts: Cohort[];
    totalUsers: number;
    avgRetentionWeek1: number;
    avgRetentionWeek2: number;
    avgRetentionWeek4: number;
    avgRetentionMonth2: number;
    engagementDistribution: Record<EngagementTier, number>;
    bestRetentionCohort: string;
    bestEngagementCohort: string;
}

export interface RetentionCurve {
    cohortLabel: string;
    dataPoints: Array<{ period: number; retentionPercent: number }>;
}

// ─── Helper Functions ───────────────────────────────────────

function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

function daysBetween(date1: Date, date2: Date): number {
    const ms = Math.abs(date2.getTime() - date1.getTime());
    return Math.floor(ms / (24 * 60 * 60 * 1000));
}

function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

function getMonthStart(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}

function toDate(ts: any): Date {
    if (!ts) return new Date();
    if (ts.toDate) return ts.toDate();
    if (ts instanceof Date) return ts;
    return new Date(ts);
}

// ─── Core Analysis ──────────────────────────────────────────

/**
 * Perform cohort analysis across a date range
 */
export async function performCohortAnalysis(
    startDate: Date,
    endDate: Date,
    period: CohortPeriod
): Promise<CohortAnalysis> {
    try {
        // 1. Fetch all sessions in date range
        const q = query(
            collection(db, SESSIONS_PATH),
            where('startedAt', '>=', Timestamp.fromDate(startDate)),
            where('startedAt', '<=', Timestamp.fromDate(endDate)),
            orderBy('startedAt', 'asc')
        );
        const snapshot = await getDocs(q);

        const sessions: OmoreSession[] = snapshot.docs.map(d => {
            const data = d.data();
            return {
                id: d.id,
                appleID: data.appleID || '',
                profileID: data.profileID || '',
                explorerName: data.explorerName || '',
                helperType: data.helperType || 'general',
                origin: data.origin || 'home',
                startedAt: data.startedAt,
                endedAt: data.endedAt,
                durationSeconds: data.durationSeconds || 0,
                messageCount: data.messageCount || 0,
                totalTokensUsed: data.totalTokensUsed || 0,
                suggestionsShown: data.suggestionsShown || 0,
                suggestionsAdopted: data.suggestionsAdopted || 0,
                hasModerationFlag: data.hasModerationFlag || false,
                deviceType: data.deviceType || 'iPhone',
                appVersion: data.appVersion || '',
            };
        });

        // 2. Group sessions by user
        const userSessionsMap = new Map<string, OmoreSession[]>();
        sessions.forEach(session => {
            if (!session.appleID) return;
            const existing = userSessionsMap.get(session.appleID) || [];
            existing.push(session);
            userSessionsMap.set(session.appleID, existing);
        });

        // 3. Build UserCohortData for each user
        const now = new Date();
        const allUserData = new Map<string, UserCohortData>();
        userSessionsMap.forEach((userSessions, appleID) => {
            const userData = buildUserCohortData(appleID, userSessions, now);
            allUserData.set(appleID, userData);
        });

        // 4. Group users into cohorts by first session date
        const cohortMap = new Map<string, UserCohortData[]>();
        allUserData.forEach(userData => {
            const firstDate = new Date(userData.firstSessionDate);
            let cohortKey: string;
            if (period === CohortPeriod.WEEKLY) {
                cohortKey = formatDate(getWeekStart(firstDate));
            } else {
                cohortKey = formatDate(getMonthStart(firstDate));
            }
            const existing = cohortMap.get(cohortKey) || [];
            existing.push(userData);
            cohortMap.set(cohortKey, existing);
        });

        // 5. Build cohorts with retention calculations
        const cohorts: Cohort[] = [];
        const sortedKeys = Array.from(cohortMap.keys()).sort();

        for (const key of sortedKeys) {
            const users = cohortMap.get(key)!;
            const cohortStartDate = new Date(key);
            let cohortEndDate: Date;
            let label: string;

            if (period === CohortPeriod.WEEKLY) {
                cohortEndDate = new Date(cohortStartDate);
                cohortEndDate.setDate(cohortEndDate.getDate() + 6);
                label = `Week of ${cohortStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
            } else {
                cohortEndDate = new Date(cohortStartDate.getFullYear(), cohortStartDate.getMonth() + 1, 0);
                label = cohortStartDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            }

            const cohort = buildCohort(
                key,
                label,
                formatDate(cohortStartDate),
                formatDate(cohortEndDate),
                users,
                userSessionsMap,
                cohortStartDate
            );
            cohorts.push(cohort);
        }

        // 6. Calculate overall metrics
        const engagementDistribution: Record<EngagementTier, number> = {
            [EngagementTier.POWER_USER]: 0,
            [EngagementTier.ACTIVE]: 0,
            [EngagementTier.REGULAR]: 0,
            [EngagementTier.TRIAL]: 0,
            [EngagementTier.CHURNED]: 0,
        };

        allUserData.forEach(userData => {
            engagementDistribution[userData.engagementTier]++;
        });

        const cohortsWithRetention = cohorts.filter(c => c.userCount > 0);
        const avgRetentionWeek1 = cohortsWithRetention.length > 0
            ? cohortsWithRetention.reduce((s, c) => s + c.retention.week1, 0) / cohortsWithRetention.length
            : 0;
        const avgRetentionWeek2 = cohortsWithRetention.length > 0
            ? cohortsWithRetention.reduce((s, c) => s + c.retention.week2, 0) / cohortsWithRetention.length
            : 0;
        const avgRetentionWeek4 = cohortsWithRetention.length > 0
            ? cohortsWithRetention.reduce((s, c) => s + c.retention.week4, 0) / cohortsWithRetention.length
            : 0;
        const avgRetentionMonth2 = cohortsWithRetention.length > 0
            ? cohortsWithRetention.reduce((s, c) => s + c.retention.month2, 0) / cohortsWithRetention.length
            : 0;

        const bestRetentionCohort = cohortsWithRetention.length > 0
            ? cohortsWithRetention.reduce((best, c) => c.retention.week4 > best.retention.week4 ? c : best).cohortLabel
            : 'N/A';

        const bestEngagementCohort = cohortsWithRetention.length > 0
            ? cohortsWithRetention.reduce((best, c) => c.avgSessionsPerUser > best.avgSessionsPerUser ? c : best).cohortLabel
            : 'N/A';

        return {
            period,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            cohorts,
            totalUsers: allUserData.size,
            avgRetentionWeek1,
            avgRetentionWeek2,
            avgRetentionWeek4,
            avgRetentionMonth2,
            engagementDistribution,
            bestRetentionCohort,
            bestEngagementCohort,
        };
    } catch (error) {
        console.error('Error performing cohort analysis:', error);
        return {
            period,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            cohorts: [],
            totalUsers: 0,
            avgRetentionWeek1: 0,
            avgRetentionWeek2: 0,
            avgRetentionWeek4: 0,
            avgRetentionMonth2: 0,
            engagementDistribution: {
                [EngagementTier.POWER_USER]: 0,
                [EngagementTier.ACTIVE]: 0,
                [EngagementTier.REGULAR]: 0,
                [EngagementTier.TRIAL]: 0,
                [EngagementTier.CHURNED]: 0,
            },
            bestRetentionCohort: 'N/A',
            bestEngagementCohort: 'N/A',
        };
    }
}

/**
 * Build UserCohortData for a single user
 */
function buildUserCohortData(
    appleID: string,
    sessions: OmoreSession[],
    now: Date
): UserCohortData {
    const sorted = [...sessions].sort((a, b) => {
        const aTime = toDate(a.startedAt).getTime();
        const bTime = toDate(b.startedAt).getTime();
        return aTime - bTime;
    });

    const firstSession = toDate(sorted[0].startedAt);
    const lastSession = toDate(sorted[sorted.length - 1].startedAt);
    const daysSinceFirst = daysBetween(firstSession, now);
    const daysSinceLast = daysBetween(lastSession, now);

    const totalMessages = sorted.reduce((s, sess) => s + sess.messageCount, 0);
    const totalTokens = sorted.reduce((s, sess) => s + sess.totalTokensUsed, 0);
    const totalDuration = sorted.reduce((s, sess) => s + sess.durationSeconds, 0);
    const avgDuration = sorted.length > 0 ? totalDuration / sorted.length : 0;

    const helperTypes = new Set<string>();
    sorted.forEach(s => helperTypes.add(s.helperType));

    // Engagement tier
    let tier: EngagementTier;
    if (daysSinceLast > 30) {
        tier = EngagementTier.CHURNED;
    } else if (sorted.length >= 10) {
        tier = EngagementTier.POWER_USER;
    } else if (sorted.length >= 5) {
        tier = EngagementTier.ACTIVE;
    } else if (sorted.length >= 2) {
        tier = EngagementTier.REGULAR;
    } else {
        tier = EngagementTier.TRIAL;
    }

    // Retention rate: % of weeks active since joining
    const weeksSinceJoining = Math.max(1, Math.floor(daysSinceFirst / 7));
    const activeWeeks = new Set<number>();
    sorted.forEach(s => {
        const sessionDate = toDate(s.startedAt);
        const weekNum = Math.floor(daysBetween(firstSession, sessionDate) / 7);
        activeWeeks.add(weekNum);
    });
    const retentionRate = (activeWeeks.size / weeksSinceJoining) * 100;

    return {
        appleID,
        explorerName: sorted[0].explorerName || appleID,
        firstSessionDate: formatDate(firstSession),
        lastSessionDate: formatDate(lastSession),
        totalSessions: sorted.length,
        totalMessages,
        totalTokens,
        avgSessionDuration: avgDuration,
        helperTypesUsed: Array.from(helperTypes),
        engagementTier: tier,
        daysSinceFirstSession: daysSinceFirst,
        daysSinceLastSession: daysSinceLast,
        retentionRate,
    };
}

/**
 * Build a Cohort with retention calculations
 */
function buildCohort(
    cohortId: string,
    label: string,
    startDate: string,
    endDate: string,
    users: UserCohortData[],
    userSessionsMap: Map<string, OmoreSession[]>,
    cohortStartDate: Date
): Cohort {
    const userCount = users.length;
    if (userCount === 0) {
        return {
            cohortId, cohortLabel: label, startDate, endDate, userCount: 0,
            users: [], retention: { week1: 0, week2: 0, week3: 0, week4: 0, month2: 0, month3: 0 },
            avgSessionsPerUser: 0, avgMessagesPerUser: 0, avgTokensPerUser: 0,
            powerUserPercent: 0, churnedUserPercent: 0, topHelperTypes: [],
        };
    }

    // Calculate retention for each period
    const retention = calculateCohortRetention(users, userSessionsMap, cohortStartDate);

    // Engagement metrics
    const totalSessions = users.reduce((s, u) => s + u.totalSessions, 0);
    const totalMessages = users.reduce((s, u) => s + u.totalMessages, 0);
    const totalTokens = users.reduce((s, u) => s + u.totalTokens, 0);

    const powerUsers = users.filter(u => u.engagementTier === EngagementTier.POWER_USER).length;
    const churnedUsers = users.filter(u => u.engagementTier === EngagementTier.CHURNED).length;

    // Top helper types
    const helperTypeCounts: Record<string, number> = {};
    users.forEach(u => {
        u.helperTypesUsed.forEach(ht => {
            helperTypeCounts[ht] = (helperTypeCounts[ht] || 0) + 1;
        });
    });
    const topHelperTypes = Object.entries(helperTypeCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([helperType, count]) => ({
            helperType,
            userCount: count,
            percentage: (count / userCount) * 100,
        }));

    return {
        cohortId,
        cohortLabel: label,
        startDate,
        endDate,
        userCount,
        users,
        retention,
        avgSessionsPerUser: totalSessions / userCount,
        avgMessagesPerUser: totalMessages / userCount,
        avgTokensPerUser: totalTokens / userCount,
        powerUserPercent: (powerUsers / userCount) * 100,
        churnedUserPercent: (churnedUsers / userCount) * 100,
        topHelperTypes,
    };
}

/**
 * Calculate retention percentages for each time period
 */
function calculateCohortRetention(
    users: UserCohortData[],
    userSessionsMap: Map<string, OmoreSession[]>,
    cohortStartDate: Date
): Cohort['retention'] {
    const userCount = users.length;
    if (userCount === 0) {
        return { week1: 0, week2: 0, week3: 0, week4: 0, month2: 0, month3: 0 };
    }

    const periods = [
        { key: 'week1' as const, startDay: 7, endDay: 14 },
        { key: 'week2' as const, startDay: 14, endDay: 21 },
        { key: 'week3' as const, startDay: 21, endDay: 28 },
        { key: 'week4' as const, startDay: 28, endDay: 35 },
        { key: 'month2' as const, startDay: 30, endDay: 60 },
        { key: 'month3' as const, startDay: 60, endDay: 90 },
    ];

    const retention: Record<string, number> = {};

    for (const period of periods) {
        const windowStart = new Date(cohortStartDate);
        windowStart.setDate(windowStart.getDate() + period.startDay);
        const windowEnd = new Date(cohortStartDate);
        windowEnd.setDate(windowEnd.getDate() + period.endDay);

        let returnedUsers = 0;
        for (const user of users) {
            const sessions = userSessionsMap.get(user.appleID) || [];
            const hasSessionInWindow = sessions.some(s => {
                const sessionDate = toDate(s.startedAt);
                return sessionDate >= windowStart && sessionDate < windowEnd;
            });
            if (hasSessionInWindow) returnedUsers++;
        }

        retention[period.key] = (returnedUsers / userCount) * 100;
    }

    return retention as Cohort['retention'];
}

// ─── Retention Curves ───────────────────────────────────────

/**
 * Generate chart-friendly retention curve data
 */
export function generateRetentionCurves(analysis: CohortAnalysis): RetentionCurve[] {
    return analysis.cohorts.map(cohort => ({
        cohortLabel: cohort.cohortLabel,
        dataPoints: [
            { period: 0, retentionPercent: 100 },
            { period: 1, retentionPercent: cohort.retention.week1 },
            { period: 2, retentionPercent: cohort.retention.week2 },
            { period: 3, retentionPercent: cohort.retention.week3 },
            { period: 4, retentionPercent: cohort.retention.week4 },
        ],
    }));
}
